import {
    createRequestFilters,
    fetchAllPagesWithRateLimit,
    FilterMethod,
    flattenRequestFilters,
    ICustomFilter,
    ListProviderMessagesOrderEnum,
    ListProviderMessagesRequest,
    MeInfoResponse,
    MessageCategory,
    MessageLinkEntityType,
    MessagePurposeType,
    ProviderComment,
    ProviderDocument,
    ProviderDocumentUrl,
    ProviderMessage,
    ProviderMessageSortType,
    ProviderUser,
    runFireAndForget,
} from '@enter/core';
import _ from 'lodash';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { MessageGenerator } from './message.generator';
import { MessageService } from './message.service';
import { IFeatures, RootStore } from './root.store';
import { appendTo, equalsFilter, parseItemListingRequest, replaceInArray } from './util';

export interface IMessageListingRequest {
    page?: number;
    pageSize?: number;
    sort?: ProviderMessageSortType[];
    order?: ListProviderMessagesOrderEnum[];
    filters?: ICustomFilter[];
}

export interface IMessageListing {
    messages: ProviderMessage[];
    total: number;
}

export interface IMessageDetail {
    message?: ProviderMessage;
    comments?: ProviderComment[];
    documents?: ProviderDocument[];
}

export interface IMessageCompose {
    subject: string;
    body: string;
    claimIds?: string[];
    files?: File[];
    priority?: string;
    dueDate?: string;
    categoryIds?: string[];
    assignedUserIds?: string[];
    purposeType?: MessagePurposeType;
}

export interface ICommentCompose {
    messageId: string;
    body: string;
    files?: File[];
}

export interface ICommentAttachment {
    document: ProviderDocument;
    documentUrl: ProviderDocumentUrl;
}

export interface IMessageFilter {
    open?: boolean;
    read?: boolean;
    claimId?: string;
}

export class MessageStore {
    @observable public unreadInboxTotal: number = 0;
    /** Server-derived unread count for Inbox, respecting currently active filters (not just loaded rows). */
    @observable public unreadInboxFilteredTotal: number = 0;
    /** Server-derived unread count for Done tab, respecting currently active filters. */
    @observable public unreadDoneFilteredTotal: number = 0;
    /** Server-derived total count for Inbox ignoring the read filter (all read states). */
    @observable public allInboxFilteredTotal: number = 0;
    /** Server-derived total count for Done tab ignoring the read filter (all read states). */
    @observable public allDoneFilteredTotal: number = 0;
    @observable public doneTotal: number = 0;
    @observable public doneMessages: ProviderMessage[] = [];
    @observable public inboxTotal: number = 0;
    @observable public inboxMessages: ProviderMessage[] = [];
    @observable public comments: ProviderComment[] = [];
    @observable public documents: ProviderDocument[] = [];
    @observable public documentUrls: ProviderDocumentUrl[] = [];
    @observable public messageCategories: MessageCategory[] = [];
    @observable public providerUsers: ProviderUser[] = [];

    // mapping messageId to array of commentIds
    @observable public commentIndex: Map<string, string[]> = new Map<string, string[]>();

    private features: IFeatures;
    private messageGenerator: MessageGenerator;
    private messageService: MessageService = new MessageService();
    private lastListing: IMessageListingRequest | undefined;
    private documentFiles = new Map<string, ProviderDocumentUrl>();
    private rootStore: RootStore;
    /** Tracks read state set by markAsRead/markAsUnread so we don't mutate list until updateMessageInList */
    @observable public lastKnownReadByMessageId: Record<string, boolean> = {};

    constructor(features: IFeatures, messageGenerator: MessageGenerator, rootStore: RootStore) {
        this.features = features;
        this.messageGenerator = messageGenerator;
        this.rootStore = rootStore;
        // MobX 6+ requires makeObservable for decorators to work - without it MessageList won't re-render
        makeObservable(this);
    }

    public getUnreadTotal = async (): Promise<void> => {
        const req = {
            limit: 1,
            filterOpen: 'EQ:1',
            filterRead: 'EQ:0',
        };
        const count = await (this.features.isDemo
            ? this.messageGenerator.loadMessageCount(req)
            : this.messageService.fetchMessageCount(req));
        runInAction(() => {
            this.unreadInboxTotal = count;
        });
    };

    /**
     * Refresh unread inbox count from server. Call after markAsRead/markAsUnread
     * so the tab label and filter dropdown count stay in sync.
     */
    public refreshUnreadInboxCount = (): void => {
        runFireAndForget(() => this.getUnreadTotal(), 'refreshUnreadInboxCount');
    };

    /**
     * Fetch the unread count for the given listing request, respecting its active filters
     * but ignoring any filterRead selection. When a readFilter IS active, also fetches the
     * all-without-readfilter count (since the listing total is then a filtered subset).
     * When no readFilter is active, loadMessageListing already provides the all-total via
     * allInboxFilteredTotal/allDoneFilteredTotal, so no extra API call is needed.
     */
    public refreshReadFilterCounts = async (mlr: IMessageListingRequest, isDone: boolean): Promise<void> => {
        const baseFilters = (mlr.filters || []).filter(f => f.filterId !== 'filterRead');
        const hasReadFilter = (mlr.filters || []).some(f => f.filterId === 'filterRead');

        const unreadReq: IMessageListingRequest = {
            ...mlr,
            page: 0,
            pageSize: 1,
            filters: [
                ...baseFilters,
                { filterId: 'filterRead', filterMethod: FilterMethod.EQ, filterType: 'String', value: '0' },
            ],
        };

        if (hasReadFilter) {
            const allReq: IMessageListingRequest = { ...mlr, page: 0, pageSize: 1, filters: baseFilters };
            const [unreadCount, allCount] = await Promise.all([
                this.features.isDemo
                    ? this.messageGenerator.loadMessageCount(mlr2lpmr(unreadReq))
                    : this.messageService.fetchMessageCount(mlr2lpmr(unreadReq)),
                this.features.isDemo
                    ? this.messageGenerator.loadMessageCount(mlr2lpmr(allReq))
                    : this.messageService.fetchMessageCount(mlr2lpmr(allReq)),
            ]);
            runInAction(() => {
                if (isDone) {
                    this.unreadDoneFilteredTotal = unreadCount;
                    this.allDoneFilteredTotal = allCount;
                } else {
                    this.unreadInboxFilteredTotal = unreadCount;
                    this.allInboxFilteredTotal = allCount;
                }
            });
        } else {
            const unreadCount = await (this.features.isDemo
                ? this.messageGenerator.loadMessageCount(mlr2lpmr(unreadReq))
                : this.messageService.fetchMessageCount(mlr2lpmr(unreadReq)));
            runInAction(() => {
                if (isDone) {
                    this.unreadDoneFilteredTotal = unreadCount;
                } else {
                    this.unreadInboxFilteredTotal = unreadCount;
                }
            });
        }
    };

    /** Unread count for Done tab (derived from doneMessages + lastKnownReadByMessageId). */
    @computed
    public get unreadDoneCount(): number {
        return this.doneMessages.filter(m => !this.isMessageRead(m)).length;
    }

    /** Unread count for Inbox tab (derived from inboxMessages + lastKnownReadByMessageId). */
    @computed
    public get unreadInboxFilteredCount(): number {
        return this.inboxMessages.filter(m => !this.isMessageRead(m)).length;
    }

    private isMessageRead(message: ProviderMessage): boolean {
        if (!message.id) return message.read;
        const overridden = this.lastKnownReadByMessageId[message.id];
        return overridden !== undefined ? overridden : message.read;
    }

    public async loadMessagesForClaim(claimId: string): Promise<void> {
        if (!this.features.isDemo) {
            const messages = await this.messageService.fetchMessages({
                filterClaimId: equalsFilter(claimId),
            });
            runInAction(() => {
                // Properly separate messages by their open status
                if (messages) {
                    const inboxMsgs = _.filter(messages, { open: true });
                    const doneMsgs = _.filter(messages, { open: false });

                    this.inboxMessages = inboxMsgs;
                    this.doneMessages = doneMsgs;
                    this.inboxTotal = inboxMsgs.length;
                    this.doneTotal = doneMsgs.length;
                } else {
                    this.inboxMessages = [];
                    this.doneMessages = [];
                    this.inboxTotal = 0;
                    this.doneTotal = 0;
                }
            });
        }
    }

    public async loadMessageListing(mlr: IMessageListingRequest, forceFetch?: boolean): Promise<IMessageListing> {
        const lpmr = mlr2lpmr(mlr);
        const messageListing = await this.fetchMessageListing(mlr, forceFetch);
        const { messages, total } = messageListing;
        const isFirstPage = (mlr.page || 0) === 0;

        runInAction(() => {
            // filterOpen 'EQ:1' = inbox (open), 'EQ:0' = done (closed)
            // When filterOpen is missing, default to inbox (common for initial load)
            const isFilterOpen = _.matchesProperty('filterOpen', 'EQ:1');
            const isFilterDone = _.matchesProperty('filterOpen', 'EQ:0');
            const targetInbox = isFilterOpen(lpmr) || (!isFilterDone(lpmr) && !lpmr.filterOpen);
            // When no readFilter is active the listing total represents all messages —
            // piggyback on it to keep dropdown "all" counts accurate without an extra API call.
            const hasReadFilter = (mlr.filters || []).some(f => f.filterId === 'filterRead');
            if (targetInbox) {
                // For first page, replace messages. For subsequent pages, append.
                if (isFirstPage) {
                    this.inboxMessages = messages;
                } else {
                    // Append new messages, avoiding duplicates by ID
                    this.inboxMessages = appendTo<ProviderMessage>(this.inboxMessages, messages);
                }
                this.inboxTotal = total;
                if (!hasReadFilter) {
                    this.allInboxFilteredTotal = total;
                }
            } else {
                // For first page, replace messages. For subsequent pages, append.
                if (isFirstPage) {
                    this.doneMessages = messages;
                } else {
                    this.doneMessages = appendTo<ProviderMessage>(this.doneMessages, messages);
                }
                this.doneTotal = total;
                if (!hasReadFilter) {
                    this.allDoneFilteredTotal = total;
                }
            }
            this.lastListing = mlr;
        });
        return messageListing;
    }

    /**
     * Stream every message for CSV export without mutating inbox/done observables.
     * Omits filterOpen so both inbox and done are returned in one listing.
     * Uses limit=100, 5 concurrent requests (sliding window), ≤100 req/min.
     */
    public async fetchAllMessagesForExport(
        mlr: Omit<IMessageListingRequest, 'page' | 'pageSize'>,
        options: {
            onProgress?: (loaded: number, total: number) => void;
            signal?: AbortSignal;
            /** Called immediately before each listing request. */
            beforeFetchPage?: (page: number) => void | Promise<void>;
            onPage: (
                items: ProviderMessage[],
                info: { page: number; total: number; loaded: number },
            ) => void | Promise<void>;
        },
    ): Promise<void> {
        const filters = (mlr.filters || []).filter(f => f.filterId !== 'filterOpen');

        await fetchAllPagesWithRateLimit<ProviderMessage>({
            pageSize: 100,
            concurrency: 5,
            maxRequestsPerMinute: 100,
            retainItems: false,
            onProgress: options.onProgress,
            signal: options.signal,
            beforeFetchPage: options.beforeFetchPage,
            onPage: options.onPage,
            getId: m => m.id,
            fetchPage: async (page, pageSize) => {
                const listing = await this.fetchMessageListing({
                    ...mlr,
                    page,
                    pageSize,
                    filters,
                });
                return { items: listing.messages, total: listing.total };
            },
        });
    }

    public async fetchMessageListing(mlr: IMessageListingRequest, forceFetch?: boolean): Promise<IMessageListing> {
        const lpmr = mlr2lpmr(mlr);
        const messageListing = await (this.features.isDemo
            ? this.messageGenerator.loadMessageListing(lpmr)
            : this.messageService.fetchMessageListing(lpmr));
        if (!messageListing) {
            throw new Error(`No messages available`);
        }
        return messageListing;
    }

    public async loadMessage(messageId: string, forceFetch?: boolean): Promise<ProviderMessage | undefined> {
        const gpmr = {
            id: messageId,
        };
        const message = await (this.features.isDemo
            ? this.messageGenerator.loadMessage(gpmr)
            : this.messageService.fetchMessage(gpmr));
        if (!message) {
            throw new Error(`No messages available`);
        }
        return message;
    }

    public async loadMessageDetail(messageId: string, forceFetch?: boolean): Promise<IMessageDetail> {
        const message = await this.loadMessage(messageId);
        if (!message) {
            throw new Error(`No message for ID: ${messageId}`);
        }

        const messageDetail = await (this.features.isDemo
            ? this.messageGenerator.loadMessageDetail(messageId)
            : this.messageService.fetchMessageDetail(message));
        if (!messageDetail) {
            throw new Error(`No message detail for ID: ${messageId}`);
        }
        const { comments, documents } = messageDetail;
        runInAction(() => {
            // Do NOT modify inboxMessages/doneMessages order here.
            // The caller (e.g., refreshMessageDetail) will sync the updated message
            // back to the lists via updateMessageInList to preserve ordering.
            this.comments = appendTo<ProviderComment>(this.comments, comments);
            this.documents = appendTo<ProviderDocument>(this.documents, documents);
        });
        return messageDetail;
    }

    @action
    public async loadDocumentUrl(documentId?: string): Promise<string | undefined> {
        let documentUrl = _.find(this.documentUrls, { documentId });
        if (documentUrl) {
            return documentUrl.url;
        }
        if (documentId) {
            documentUrl = await (this.features.isDemo
                ? this.messageGenerator.loadDocumentUrl(documentId)
                : this.messageService.fetchDocumentUrl(documentId));
            if (documentUrl) {
                runInAction(() => {
                    this.documentUrls = appendTo<ProviderDocumentUrl>(this.documentUrls, documentUrl, 'url');
                });
                return documentUrl.url;
            }
        }
    }
    @action
    public async loadDocument(id: string): Promise<ProviderDocument | undefined> {
        let document = _.find(this.documents, { id });

        if (document) {
            return document;
        }

        document = await (this.features.isDemo
            ? this.messageGenerator.loadDocument(id)
            : this.messageService.fetchDocument(id));

        if (document) {
            runInAction(() => {
                this.documents = appendTo<ProviderDocument>(this.documents, document, 'id');
            });

            return document;
        }
    }

    @action
    public async composeMessage(
        account: MeInfoResponse | undefined,
        subject: string,
        body: string,
        claimIds?: string[],
        files?: File[],
        priority?: string,
        dueDate?: string,
        categoryIds?: string[],
        assignedUserIds?: string[],
        isPrivateNote?: boolean,
    ): Promise<string | undefined> {
        const messageCompose = {
            subject,
            body: body || ' ',
            claimIds, // Pass all claim IDs for linking via /link endpoint
            files,
            priority,
            dueDate,
            categoryIds,
            assignedUserIds,
            purposeType: isPrivateNote ? MessagePurposeType.PROVIDEROWNED : undefined,
        };
        const composed = await (this.features.isDemo
            ? this.messageGenerator.composeMessage(messageCompose, account)
            : this.messageService.composeMessage(messageCompose));

        if (this.lastListing) {
            await this.loadMessageListing({
                ...this.lastListing,
                page: 0,
                filters: [
                    {
                        filterId: 'filterOpen',
                        filterMethod: FilterMethod.EQ,
                        filterType: 'String',
                        value: '1',
                    },
                    // ...(this.lastListing.filters || []),
                ],
            });
        }

        return composed?.message?.id;
    }

    @action
    public async addComment(
        account: MeInfoResponse | undefined,
        messageId: string,
        body: string,
        files?: File[],
    ): Promise<void> {
        let message = _.find(_.concat([], this.doneMessages, this.inboxMessages), { id: messageId });

        // If message not found in store, try to load it
        if (!message) {
            try {
                const messageDetail = await this.loadMessageDetail(messageId);
                message = messageDetail.message;

                if (message) {
                    runInAction(() => {
                        const wasInStore = !!_.find(_.concat([], this.doneMessages, this.inboxMessages), {
                            id: messageId,
                        });
                        if (message.open) {
                            this.inboxMessages = appendTo<ProviderMessage>(this.inboxMessages, message);
                            if (!wasInStore) {
                                this.inboxTotal += 1;
                            }
                        } else {
                            this.doneMessages = appendTo<ProviderMessage>(this.doneMessages, message);
                            if (!wasInStore) {
                                this.doneTotal += 1;
                            }
                        }
                    });
                }
            } catch (error) {
                throw error;
            }
        }

        if (!message) {
            throw new Error(`Unable to load message with id '${messageId}' for adding a comment.`);
        }

        const commentCompose = {
            messageId,
            body,
            files,
        };
        const comment = await (this.features.isDemo
            ? this.messageGenerator.composeComment(commentCompose, account)
            : this.messageService.composeComment(commentCompose));
        runInAction(() => {
            this.comments = appendTo<ProviderComment>(this.comments, comment);
            const indexKey = message && message.id ? message.id : messageId;
            // this.updateIndex(this.commentIndex, indexKey, [comment]);
        });
    }

    @action
    public async markAsOpen(id?: string): Promise<void> {
        const message = this.findMessage(id);
        if (id && message && !message.open) {
            await (this.features.isDemo ? this.messageGenerator.markAsOpen(id) : this.messageService.markAsOpen(id));
            runInAction(() => {
                // message was closed (Done), now mark open (Inbox)
                message.open = true;
                // remove from Done - use filter to create new array reference
                this.doneMessages = _.filter(this.doneMessages, m => m.id !== id);
                this.doneTotal -= 1;
                // add to Inbox
                this.inboxMessages = _.concat(this.inboxMessages, message);
                this.inboxTotal += 1;
            });
        }
    }

    @action
    public async markAsClosed(id?: string): Promise<void> {
        const message = this.findMessage(id);
        if (id && message && message.open) {
            await (this.features.isDemo
                ? this.messageGenerator.markAsClosed(id)
                : this.messageService.markAsClosed(id));
            runInAction(() => {
                // message was open (Inbox), now mark closed (Done)
                message.open = false;
                // remove from Inbox - use filter to create new array reference
                this.inboxMessages = _.filter(this.inboxMessages, m => m.id !== id);
                this.inboxTotal -= 1;
                // add to Done
                this.doneMessages = _.concat(this.doneMessages, message);
                this.doneTotal += 1;
            });
        }
    }

    @action
    public async markAsRead(id?: string): Promise<void> {
        const message = this.findMessage(id);
        const { roleId } = this.rootStore;
        const isReadOnly: boolean = roleId === 'PROVIDER_READ_ACCESS';
        const effectiveRead = id ? (this.lastKnownReadByMessageId[id] ?? message?.read) : true;
        if (id && message && !effectiveRead && !isReadOnly) {
            await (this.features.isDemo ? this.messageGenerator.markAsRead(id) : this.messageService.markAsRead(id));
            runInAction(() => {
                this.lastKnownReadByMessageId[id] = true;
                if (message.open) {
                    this.unreadInboxTotal = Math.max(0, this.unreadInboxTotal - 1);
                    this.unreadInboxFilteredTotal = Math.max(0, this.unreadInboxFilteredTotal - 1);
                } else {
                    this.unreadDoneFilteredTotal = Math.max(0, this.unreadDoneFilteredTotal - 1);
                }
            });
            // Backend sync to ensure counts stay correct even if local math drifts.
            this.refreshUnreadInboxCount();
        }
    }

    @action
    public async markAsUnread(id?: string): Promise<void> {
        const message = this.findMessage(id);
        const { roleId } = this.rootStore;
        const isReadOnly: boolean = roleId === 'PROVIDER_READ_ACCESS';
        const effectiveRead = id ? (this.lastKnownReadByMessageId[id] ?? message?.read) : false;
        if (id && message && effectiveRead && !isReadOnly) {
            await (this.features.isDemo
                ? this.messageGenerator.markAsUnread(id)
                : this.messageService.markAsUnread(id));
            runInAction(() => {
                this.lastKnownReadByMessageId[id] = false;
                if (message.open) {
                    this.unreadInboxTotal += 1;
                    this.unreadInboxFilteredTotal += 1;
                } else {
                    this.unreadDoneFilteredTotal += 1;
                }
            });
            // Backend sync to ensure counts stay correct even if local math drifts.
            this.refreshUnreadInboxCount();
        }
    }

    public findMessage = (messageId: string | undefined): ProviderMessage | undefined => {
        const p = { id: messageId };
        const i = _.find<ProviderMessage>(this.inboxMessages, p);
        if (i) {
            return i;
        }
        const d = _.find<ProviderMessage>(this.doneMessages, p);
        if (d) {
            return d;
        }
    };
    public findMessages = (filter: IMessageFilter): ProviderMessage[] => {
        const p = { ...filter };
        const i = _.filter<ProviderMessage>(this.inboxMessages, p);
        const d = _.filter<ProviderMessage>(this.doneMessages, p);
        return _.concat<ProviderMessage>([], i, d);
    };

    /**
     * Update a message in the inbox/done lists with new data.
     * This syncs changes made in the detail panel back to the table.
     */
    @action
    public updateMessageInList = (updatedMessage: ProviderMessage): void => {
        if (!updatedMessage.id) return;

        this.lastKnownReadByMessageId[updatedMessage.id] = updatedMessage.read;

        // Try replace in inbox first
        const nextInbox = replaceInArray(this.inboxMessages, updatedMessage, m => m.id as string);
        if (nextInbox !== this.inboxMessages) {
            this.inboxMessages = nextInbox;
            return;
        }

        // Then try replace in done
        const nextDone = replaceInArray(this.doneMessages, updatedMessage, m => m.id as string);
        if (nextDone !== this.doneMessages) {
            this.doneMessages = nextDone;
        }
    };

    /**
     * Update message priority/dueDate and sync to list.
     */
    @action
    public async updateMessage(
        id: string,
        payload: { priority?: string; dueDate?: string | null },
        skipReload?: boolean,
    ): Promise<void> {
        await (this.features.isDemo
            ? Promise.resolve() // Demo mode doesn't support update
            : this.messageService.updateMessage(id, payload));

        // Skip reload if requested (e.g., when caller will refresh separately)
        if (skipReload) {
            return;
        }

        // Fetch fresh message and sync to list
        const message = await this.loadMessage(id);
        if (message) {
            this.updateMessageInList(message);
        }
    }

    /**
     * Link entities (claims, categories, users) to a message.
     */
    @action
    public async linkEntityToMessage(
        id: string,
        entityType: MessageLinkEntityType,
        entityIds: string[],
        skipReload?: boolean,
    ): Promise<void> {
        await (this.features.isDemo
            ? Promise.resolve()
            : this.messageService.linkEntityToMessage(id, entityType, entityIds));

        if (skipReload) {
            return;
        }

        const message = await this.loadMessage(id);
        if (message) {
            this.updateMessageInList(message);
        }
    }

    /**
     * Unlink entities (claims, categories, users) from a message.
     */
    @action
    public async unlinkEntityFromMessage(
        id: string,
        entityType: MessageLinkEntityType,
        entityIds: string[],
        skipReload?: boolean,
    ): Promise<void> {
        await (this.features.isDemo
            ? Promise.resolve()
            : this.messageService.unlinkEntityFromMessage(id, entityType, entityIds));

        if (skipReload) {
            return;
        }

        const message = await this.loadMessage(id);
        if (message) {
            this.updateMessageInList(message);
        }
    }

    public findCommentByMessageId = (messageId: string): ProviderComment | undefined => {
        return _.first(this.findCommentsByMessageId(messageId));
    };
    public findCommentsByMessageId = (messageId: string): ProviderComment[] => {
        const commentIds = this.commentIndex.get(messageId);
        return _.compact(_.map(commentIds, commentId => _.find(this.comments, { id: commentId })));
    };

    public findAttachments = (documentIds: string[]): ICommentAttachment[] => {
        return _.compact(
            _.map(documentIds, id => {
                const document = _.find(this.documents, { id });
                const documentUrl = _.find(this.documentUrls, { documentId: id });
                if (document && documentUrl) {
                    return {
                        document,
                        documentUrl,
                    };
                }
            }),
        );
    };

    private updateIndex(map: Map<string, string[]>, key: string, values?: { id?: string }[]): void {
        if (values) {
            const newValues: string[] = _.compact(_.map(values, 'id'));
            const oldValues: string[] = map.get(key) || [];
            map.set(key, _.uniq(_.concat(oldValues, newValues)));
        }
    }

    public embed = async (id: string): Promise<ProviderDocumentUrl> => {
        const cachedDocument = this.documentFiles.get(id);
        if (cachedDocument) {
            return cachedDocument;
        }
        const url = await (this.features.isDemo ? this.messageGenerator.embed(id) : this.messageService.embed(id));

        // cache files
        this.documentFiles.set(id, url);

        return url;
    };

    public download = async (id: string): Promise<ProviderDocumentUrl> => {
        return await (this.features.isDemo ? this.messageGenerator.download(id) : this.messageService.download(id));
    };

    @action
    public async loadMessageCategories(): Promise<void> {
        if (!this.features.isDemo && this.messageCategories.length === 0) {
            try {
                const categories = await this.messageService.fetchMessageCategories();
                runInAction(() => {
                    this.messageCategories = categories || [];
                });
            } catch (error) {
                console.error('Failed to load message categories', error);
                runInAction(() => {
                    this.messageCategories = [];
                });
            }
        }
    }

    public getCategoryName = (categoryId: string): string | undefined => {
        if (!categoryId) {
            return undefined;
        }
        const category = _.find(this.messageCategories, { id: categoryId });
        return category?.name;
    };

    @action
    public async loadProviderUsers(): Promise<void> {
        if (!this.features.isDemo && this.providerUsers.length === 0) {
            try {
                const users = await this.messageService.fetchProviderUsers();
                runInAction(() => {
                    this.providerUsers = users || [];
                });
            } catch (error) {
                runInAction(() => {
                    this.providerUsers = [];
                });
            }
        }
    }

    public getUserName = (userId: string): string | undefined => {
        if (!userId) {
            return undefined;
        }
        const user = _.find(this.providerUsers, u => u.userId === userId || u.personalAccountId === userId);
        if (user) {
            const fullName = _.compact([user.first, user.last]).join(' ');
            return fullName || undefined;
        }
        return undefined;
    };
}

// Convert from IMessageListingRequest to ListProviderMessagesRequest
export const mlr2lpmr = (ilr: IMessageListingRequest): ListProviderMessagesRequest => {
    const base = parseItemListingRequest(ilr) as ListProviderMessagesRequest;
    const filters = createRequestFilters(ilr.filters, true);

    // NB: for ListProviderMessagesRequest, filterRead and filterOpen are string, not string[]
    flattenRequestFilters(filters, ['filterOpen', 'filterRead']);

    return {
        ...base,
        ...filters,
    };
};
